import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Dialog, EmptyState, Field, Input, Spinner } from '@zt/ui';
import { Table, td, tdR, th, thR } from '../components/reports/blocks';
import { istanbulToday } from '../components/reports/period';
import { api, ApiError, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { toMinor } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { ReceiptResult, ReceivableList } from '../lib/types-faz8';

/**
 * Mal kabul (F8-06, ADR-807): tedarikçiye verilmiş, tamamı alınmamış siparişler (maliyetsiz) ve mal kabul formu.
 * Form, satın alma siparişi ayrıntısında da kullanılır (maliyet görme yetkisiyle faturadaki birim maliyet girilir).
 */

export interface ReceivableOrder {
  id: string;
  number: string;
  currency?: string;
  lines: Array<{ id: string; sku: string; title: string; remaining: number; unitCostMinor?: number }>;
}

export function GoodsReceiptPage() {
  const t = useT();
  const { formatDate, formatNumber } = useI18n();
  const [receiving, setReceiving] = useState<ReceivableOrder | null>(null);
  const q = useQuery({
    queryKey: ['receivable'],
    queryFn: () => api.get<ReceivableList>('/v1/admin/purchase-orders/receivable'),
  });
  const today = istanbulToday();
  return (
    <PageFrame helpKey="page.goods_receipt">
      <p className="mb-4 max-w-[80ch] text-sm text-ink-2">{t('admin.purchasing.receivableLead')}</p>
      {q.isError ? (
        <p role="alert" className="text-sm text-rose">
          {q.error instanceof ApiError ? q.error.title : String(q.error)}
        </p>
      ) : !q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !q.data.length ? (
        <EmptyState title={t('admin.purchasing.receivableEmpty')} />
      ) : (
        <Table
          caption={t('admin.purchasing.navReceive')}
          head={
            <tr>
              {(['number', 'supplier', 'warehouse', 'expected', 'lines'] as const).map((k) => (
                <th key={k} scope="col" className={k === 'lines' ? thR : th}>
                  {t(`admin.purchasing.receivableCols.${k}`)}
                </th>
              ))}
              <th scope="col" className={th}>
                <span className="sr-only">{t('admin.purchasing.receive')}</span>
              </th>
            </tr>
          }
        >
          {q.data.map((o) => {
            const open = o.lines.filter((l) => l.remaining > 0);
            const late = o.expectedDate !== null && o.expectedDate < today;
            return (
              <tr key={o.id} className="border-t border-line">
                <th scope="row" className={`${td} text-start font-medium`}>
                  {o.number}
                </th>
                <td className={td}>{o.supplierName}</td>
                <td className={td}>{o.warehouseName}</td>
                <td className={`${td} whitespace-nowrap`}>
                  {o.expectedDate ? formatDate(o.expectedDate, false) : '—'}
                  {late && (
                    <span className="block text-xs text-amber">{t('admin.purchasing.overdueMark')}</span>
                  )}
                </td>
                <td className={tdR}>
                  {t('admin.purchasing.remainingLines', {
                    count: formatNumber(open.length, 0),
                    units: formatNumber(
                      open.reduce((a, l) => a + l.remaining, 0),
                      0,
                    ),
                  })}
                </td>
                <td className={td}>
                  <Button size="sm" variant="primary" onClick={() => setReceiving(o)}>
                    {t('admin.purchasing.receive')}
                  </Button>
                </td>
              </tr>
            );
          })}
        </Table>
      )}
      {receiving && <ReceiveDialog order={receiving} onClose={() => setReceiving(null)} />}
    </PageFrame>
  );
}

export function ReceiveDialog({ order, onClose }: { order: ReceivableOrder; onClose: () => void }) {
  const t = useT();
  const { can } = usePermissions();
  const canCost = can('pricing.cost.read') && order.currency !== undefined;
  const manager = can('purchasing.read');
  const [idem] = useState(() => new IdempotencyKey());
  const [receivedOn, setReceivedOn] = useState(istanbulToday());
  const [invoiceNo, setInvoiceNo] = useState('');
  const [acceptOver, setAcceptOver] = useState(false);
  const [postInvoice, setPostInvoice] = useState(true);
  const [updateCost, setUpdateCost] = useState(true);
  const [rows, setRows] = useState(() =>
    order.lines
      .filter((l) => l.remaining > 0)
      .map((l) => ({ ...l, qty: String(l.remaining), lotNo: '', expiry: '', cost: '' })),
  );
  const setRow = (i: number, k: 'qty' | 'lotNo' | 'expiry' | 'cost', v: string) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const parsed = rows.map((r) => ({
    ...r,
    n: /^\d+$/.test(r.qty.trim()) ? Number(r.qty) : NaN,
    costMinor: r.cost.trim() ? toMinor(r.cost, order.currency ?? 'TRY') : undefined,
  }));
  const valid =
    parsed.every((r) => Number.isInteger(r.n) && r.costMinor !== null) && parsed.some((r) => r.n > 0);
  const save = useApiMutation(
    () =>
      api.post<ReceiptResult>(
        `/v1/admin/purchase-orders/${order.id}/receive`,
        {
          receivedOn,
          invoiceNo: invoiceNo.trim() || null,
          acceptOver,
          postInvoice,
          updateCost,
          lines: parsed
            .filter((r) => r.n > 0)
            .map((r) => ({
              lineId: r.id,
              quantity: r.n,
              ...(r.lotNo.trim() ? { lot: { lotNo: r.lotNo.trim(), expiryDate: r.expiry || null } } : {}),
              ...(r.costMinor !== undefined ? { unitCostMinor: r.costMinor } : {}),
            })),
        },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['receivable'], ['purchase-order', order.id], ['purchase-orders'], ['supplier-balances']],
      success: t('admin.purchasing.received'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  const lineError = (i: number) => {
    const idx = parsed.filter((r) => r.n > 0).findIndex((r) => r.id === rows[i]!.id);
    return idx >= 0 ? fieldError(save.error, `lines.${idx}.quantity`) : undefined;
  };
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.purchasing.receiveTitle', { number: order.number })}
      closeLabel={t('common.close')}
      variant="sheet"
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!valid}
            onClick={() => save.mutate(undefined)}
          >
            {t('admin.purchasing.receiveSave')}
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-ink-2">{t('admin.purchasing.receiveLead')}</p>
      <div className="mb-4 grid gap-4 sm:grid-cols-2">
        <Field label={t('admin.purchasing.receivedOn')} required error={fieldError(save.error, 'receivedOn')}>
          {(p) => (
            <Input
              {...p}
              type="date"
              max={istanbulToday()}
              value={receivedOn}
              onChange={(e) => setReceivedOn(e.target.value)}
            />
          )}
        </Field>
        <Field label={t('admin.purchasing.invoiceNo')} optionalLabel={t('common.optional')}>
          {(p) => <Input {...p} value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />}
        </Field>
      </div>
      <div className="space-y-4">
        {parsed.map((r, i) => (
          <fieldset key={r.id} className="rounded-md border border-line p-3">
            <legend className="px-1 text-sm font-medium text-ink">
              {r.title} <span className="text-xs font-normal text-ink-3">({r.sku})</span>
            </legend>
            <p className="mb-2 text-xs text-ink-3">
              {t('admin.purchasing.receiveCols.remaining')}: {r.remaining}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label={t('admin.purchasing.receiveCols.quantity')}
                required
                help={i === 0 ? <HelpTip k="purchasing.receive" /> : undefined}
                error={
                  r.qty.trim() && !Number.isInteger(r.n)
                    ? t('admin.purchasing.quantityInvalid')
                    : lineError(i)
                }
              >
                {(p) => (
                  <Input
                    {...p}
                    inputMode="numeric"
                    value={r.qty}
                    onChange={(e) => setRow(i, 'qty', e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('admin.purchasing.receiveCols.lot')} optionalLabel={t('common.optional')}>
                {(p) => <Input {...p} value={r.lotNo} onChange={(e) => setRow(i, 'lotNo', e.target.value)} />}
              </Field>
              {r.lotNo.trim() && (
                <Field label={t('admin.purchasing.receiveCols.expiry')} optionalLabel={t('common.optional')}>
                  {(p) => (
                    <Input
                      {...p}
                      type="date"
                      value={r.expiry}
                      onChange={(e) => setRow(i, 'expiry', e.target.value)}
                    />
                  )}
                </Field>
              )}
              {canCost && (
                <Field
                  label={`${t('admin.purchasing.receiveCols.cost')} (${order.currency})`}
                  optionalLabel={t('common.optional')}
                  error={
                    r.cost.trim() && r.costMinor === null ? t('admin.purchasing.costInvalid') : undefined
                  }
                >
                  {(p) => (
                    <Input
                      {...p}
                      inputMode="decimal"
                      value={r.cost}
                      onChange={(e) => setRow(i, 'cost', e.target.value)}
                    />
                  )}
                </Field>
              )}
            </div>
          </fieldset>
        ))}
      </div>
      <div className="mt-4 space-y-2 text-sm">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={acceptOver} onChange={(e) => setAcceptOver(e.target.checked)} />
          {t('admin.purchasing.acceptOver')}
        </label>
        {manager && (
          <>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={postInvoice}
                onChange={(e) => setPostInvoice(e.target.checked)}
              />
              {t('admin.purchasing.postInvoice')}
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={updateCost} onChange={(e) => setUpdateCost(e.target.checked)} />
              {t('admin.purchasing.updateCost')}
              <HelpTip k="purchasing.update_cost" />
            </label>
          </>
        )}
      </div>
      {!parsed.some((r) => r.n > 0) && (
        <p role="status" className="mt-2 text-sm text-amber">
          {t('admin.purchasing.nothingToReceive')}
        </p>
      )}
    </Dialog>
  );
}
